import { Dialog as SheetPrimitive } from "radix-ui";
import React from "react";

import { Button } from "~/components/ui/button";
import { cn } from "~/components/ui/cn";
import { CloseIcon } from "~/components/ui/icons/close";

export const Sidebar = SheetPrimitive.Root;

export const SidebarTrigger = SheetPrimitive.Trigger;

export const SidebarClose = SheetPrimitive.Close;

export const SidebarPortal = SheetPrimitive.Portal;

export const SidebarOverlay: React.FC<
  React.ComponentProps<typeof SheetPrimitive.Overlay> & {
    className?: string;
  }
> = ({ className, ...props }) => {
  return (
    <SheetPrimitive.Overlay
      className={cn(
        "fixed inset-0 z-40 bg-black/30 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
        className,
      )}
      {...props}
    />
  );
};

SidebarOverlay.displayName = SheetPrimitive.Overlay.displayName;

export type SidebarProps = {
  /**
   * Define the position of the modal
   */
  left?: boolean;
};

export const SidebarContent: React.FC<
  React.ComponentProps<typeof SheetPrimitive.Content> & SidebarProps
> = ({ className, children, left = false, ...props }) => {
  return (
    <SidebarPortal>
      <SidebarOverlay />
      <SheetPrimitive.Content
        className={cn(
          "fixed inset-y-0 z-40 h-full w-screen bg-white data-[state=closed]:duration-300 data-[state=open]:duration-300 data-[state=closed]:ease-in-out-cubic data-[state=open]:ease-in-out-cubic data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
          {
            "left-0 data-[state=closed]:slide-out-to-left-full data-[state=open]:slide-in-from-left-full":
              left,
            "right-0 data-[state=closed]:slide-out-to-right-full data-[state=open]:slide-in-from-right-full":
              !left,
          },
          className,
        )}
        {...props}
      >
        {children}
        <SidebarClose asChild>
          <div
            className={cn("absolute top-0 flex pt-6", {
              "right-0 -mr-14": left,
              "left-0 -ml-14": !left,
            })}
          >
            <Button type="button" size="icon" id="sidebar__close-button">
              <CloseIcon className="size-6 text-white" />
            </Button>
          </div>
        </SidebarClose>
      </SheetPrimitive.Content>
    </SidebarPortal>
  );
};

SidebarContent.displayName = SheetPrimitive.Content.displayName;

export const SidebarTitle: React.FC<
  React.ComponentProps<typeof SheetPrimitive.Title>
> = ({ className, ...props }) => (
  <SheetPrimitive.Title
    className={cn("text-2xl font-bold text-gray-900", className)}
    {...props}
  />
);

SidebarTitle.displayName = SheetPrimitive.Title.displayName;

export const SidebarDescription: React.FC<
  React.ComponentProps<typeof SheetPrimitive.Description>
> = ({ className, ...props }) => (
  <SheetPrimitive.Description className={className} {...props} />
);

SidebarDescription.displayName = SheetPrimitive.Description.displayName;
